import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Card, CardContent, Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip
} from 'recharts';

const UsageBarChartCard = ({ title = 'Cumulative Usage', data = [], sx }) => {
  const theme = useTheme();
  const paletteMode = theme.palette.mode;
  const cardBg = paletteMode === 'light'
    ? theme.palette.background.paper
    : theme.palette.background.elevated || alpha(theme.palette.background.paper, 0.08);
  const borderColor = alpha(theme.palette.divider, paletteMode === 'light' ? 0.6 : 0.4);
  const axisColor = paletteMode === 'light' ? theme.palette.text.secondary : '#94a3b8';
  const gridColor = alpha(theme.palette.divider, paletteMode === 'light' ? 0.7 : 0.3);
  const barColor = paletteMode === 'light' ? theme.palette.primary.main : theme.palette.primary.light;
  const hoverColor = alpha(barColor, paletteMode === 'light' ? 0.25 : 0.35);
  const tooltipBg = paletteMode === 'light'
    ? '#ffffff'
    : alpha(theme.palette.background.default || '#0f172a', 0.95);
  const tooltipBorder = paletteMode === 'light' ? '#e5e7eb' : alpha('#ffffff', 0.2);

  const processedData = useMemo(
    () => data.map(({ time, value }) => ({
      time,
      value: Number.isFinite(value) ? Number(value) : 0
    })),
    [data]
  );

  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) {
      return null;
    }
    const [{ value }] = payload;
    const displayValue = Number.isFinite(value) ? Number(value) : 0;
    return (
      <Box
        sx={{
          backgroundColor: tooltipBg,
          border: `1px solid ${tooltipBorder}`,
          borderRadius: 2,
          px: 1.5,
          py: 1,
          minWidth: 120
        }}
      >
        <Typography variant="caption" sx={{ color: axisColor, fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
          {displayValue.toLocaleString(undefined, { maximumFractionDigits: 3 })} kW
        </Typography>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        width: '100%',
        flexGrow: 1,
        height: '100%',
        borderRadius: 4,
        backgroundColor: cardBg,
        border: `1px solid ${borderColor}`,
        boxShadow:
          paletteMode === 'light'
            ? '0 18px 35px rgba(15,23,42,0.08)'
            : '0 28px 45px rgba(2,6,23,0.55)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        ...sx,
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 2, md: 2.5 },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          {title}
        </Typography>
        <Box sx={{ flexGrow: 1, minHeight: 260, mt: 1, display: 'flex' }}>
        {processedData.length === 0 ? (
          <Box
            sx={{
                flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: axisColor,
              fontSize: 14,
                fontWeight: 500
            }}
          >
            No data available
          </Box>
        ) : (
            <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={processedData}
                margin={{ top: 16, right: 24, left: 10, bottom: 16 }}
                barCategoryGap={18}
            >
                <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} stroke={gridColor} />
              <XAxis
                dataKey="time"
                tick={{ fill: axisColor, fontSize: 12 }}
                stroke={gridColor}
                tickLine={false}
                axisLine={{ stroke: gridColor }}
                  interval="preserveStartEnd"
                  tickMargin={8}
              />
              <YAxis
                tick={{ fill: axisColor, fontSize: 12 }}
                stroke={gridColor}
                tickLine={false}
                axisLine={{ stroke: gridColor }}
                  width={60}
                label={{ value: 'kW', angle: -90, position: 'insideLeft', fill: axisColor, fontSize: 12 }}
              />
              <RechartsTooltip
                content={renderTooltip}
                  cursor={{ fill: hoverColor }}
                  allowEscapeViewBox={{ x: false, y: false }}
              />
              <Bar
                dataKey="value"
                fill={barColor}
                radius={[8, 8, 0, 0]}
                maxBarSize={48}
                animationDuration={900}
                animationEasing="ease-out"
                activeBar={{ fill: alpha(barColor, paletteMode === 'light' ? 0.8 : 0.9) }}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
        </Box>
      </CardContent>
    </Card>
  );
};

UsageBarChartCard.propTypes = {
  title: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      time: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.number.isRequired
    })
  ),
  sx: PropTypes.object
};

export default UsageBarChartCard;
